import axios from "axios";
import { useSyncExternalStore } from "react";
import { downloadBackup, type BackupKind } from "../api/backup";
import { extractApiError } from "./apiClient";

/**
 * The backup download lives OUTSIDE any page component, so it keeps running (and keeps reporting
 * progress) while the person navigates around the app. A small card (BackupDownloadIndicator, mounted
 * in the app layout) shows it on every page. It only stops if the browser tab is closed or reloaded,
 * which is why a warning is shown in that case while it is in progress.
 */
export type BackupDownloadState =
  | { status: "idle" }
  | { status: "preparing" }
  | { status: "downloading"; received: number; total: number }
  | { status: "done"; bytes: number; nothingNew?: boolean }
  | { status: "error"; message: string };

let state: BackupDownloadState = { status: "idle" };
const listeners = new Set<() => void>();
let resetTimer: ReturnType<typeof setTimeout> | null = null;

function set(next: BackupDownloadState) {
  state = next;
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function useBackupDownload(): BackupDownloadState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

export const isBackupDownloading = () => state.status === "preparing" || state.status === "downloading";

/** Closes a finished/failed card. */
export function dismissBackupDownload() {
  if (!isBackupDownloading()) set({ status: "idle" });
}

/** Starts the download unless one is already running. Safe to call from any page. */
export function startBackupDownload(kind: BackupKind = "FULL") {
  if (isBackupDownloading()) return;
  if (resetTimer) clearTimeout(resetTimer);
  set({ status: "preparing" });
  let lastBytes = 0;
  downloadBackup(kind, (p) => {
    lastBytes = p.received;
    set(p.received > 0 ? { status: "downloading", received: p.received, total: p.total } : { status: "preparing" });
  })
    .then(({ nothingNew }) => {
      set({ status: "done", bytes: lastBytes, nothingNew });
      resetTimer = setTimeout(() => {
        if (state.status === "done") set({ status: "idle" });
      }, 12_000);
    })
    .catch((err) => set({ status: "error", message: err instanceof Error && !axios.isAxiosError(err) ? err.message : extractApiError(err).message }));
}

// Leaving or reloading the tab would cancel the download — ask first while one is running.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => {
    if (isBackupDownloading()) {
      e.preventDefault();
      e.returnValue = "";
    }
  });
}

/** "12.3 MB" — sizes under 1 MB show in KB so a small backup doesn't read "0.0 MB". */
export function fmtMb(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Percent complete for a downloading state, capped at 99 until it's saved. */
export function backupPercent(s: BackupDownloadState): number {
  if (s.status !== "downloading") return 0;
  return Math.min(99, Math.round((s.received / Math.max(s.total, 1)) * 100));
}
